// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 5b2c3c14-ed85-4246-b556-511806948ead

// ---------------------------------------------------------------------------
// Primordia — Relationship Wizard
//
// "We have a lot of fields but nothing in them and I don't know what should be
// in them." The Relationships & Dependencies section is empty on almost every
// application, because nobody has time to link 400 apps by hand. Everything
// downstream suffers for it: the wave planner cannot sequence what it cannot
// see, the mind map draws islands, and dependency risk never fires.
//
// The insight this screen is built on: the relationships are ALREADY IN THE
// TENANT. They are sitting in the integration rows, in the `via` engine values,
// in free-text answers like "imports codes into Accounts in Epic", and in the
// 'Managed By' tag. They were simply never turned into links.
//
// So STEP 1 is deterministic and reads nothing but the tenant's own data — no
// model, no network call, and every single proposal carries the exact phrase or
// integration row it came from. That is the valuable part, and it works with
// the backend switched off.
//
// STEP 2 (optional, opt-in, clearly separated) asks /ai/suggest-relations. It
// is given the candidate target names and MAY ONLY CHOOSE FROM THAT LIST — a
// suggestion naming anything else is discarded before it is ever rendered, and
// the count of discards is shown. AI rows carry their own chip and start
// UNCHECKED. This mirrors the safety posture of AiEnrichment.js: proposed
// values never become fact without a human pressing Accept.
//
// STEP 3 merges accepted links into relations[relKey] without clobbering what
// is there, never creates a duplicate, and stamps
//   attributes.relationSource = 'inferred' | 'ai'
// so the origin of every link stays auditable. The same evidence is written to
// attributes.notes, which is a first-class relation attribute in
// RelationEditor.js, so the provenance survives later hand-editing.
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Checkbox, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel,
  InputAdornment, LinearProgress, MenuItem, Paper, Slider, Stack, Table,
  TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import HubIcon from '@mui/icons-material/Hub';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { allFieldsFor, relationMeta, schemaFor, typeMeta } from './schema';
import { TypeBadge } from './ui';
import { getConfig } from './config';
import { getIdToken } from './auth';
import store from './store';

// Optimum palette
const NAVY = '#021F4B';
const BLUE = '#0D5B9A';
const GREY = '#5C6B7A';
const AMBER = '#B26A00';
const GREEN = '#2E7D32';
const HEAD = 'sectionHeader.main';
const headSx = { '& th': { fontWeight: 700, color: HEAD, fontSize: 12, bgcolor: '#f7f9fb' } };

// Rendering guard only — every count, every bulk action and the apply step
// operate over the FULL candidate set, not the visible slice.
const ROW_CAP = 300;
// Applications per /ai/suggest-relations request, so progress means something.
const AI_CHUNK = 15;
// Upper bound on the target list we hand the model in one request.
const AI_TARGET_CAP = 400;

const norm = (s) => String(s == null ? '' : s).trim().toLowerCase();
const clean = (s) => String(s == null ? '' : s).trim();
const pct = (c) => Math.round(c * 100);

// ---------------------------------------------------------------------------
// Which relations can start FROM an Application, straight off the schema, so
// this file never invents a relation type schema.js does not declare.
// ---------------------------------------------------------------------------
const APPLICATION_RELATIONS = (() => {
  const keys = [];
  (schemaFor('Application').sections || []).forEach((s) =>
    (s.relations || []).forEach((k) => { if (!keys.includes(k)) keys.push(k); })
  );
  return keys.map((k) => relationMeta(k)).filter(Boolean);
})();

const relationsTargeting = (type) => APPLICATION_RELATIONS.filter((r) => r.target === type);

// The relation we propose by default for a target of this type. Interfaces are
// split by the direction captured on the integration row; application-to-
// application flow has no purpose-built "depends on" relation in the LeanIX
// meta-model, so it is proposed as a child dependency and the row lets the
// reviewer change it.
function defaultRelationsFor(targetType, direction) {
  if (targetType === 'Interface') {
    const d = norm(direction);
    if (d === 'inbound') return ['consumedInterfaces'];
    if (d === 'outbound') return ['providedInterfaces'];
    return ['consumedInterfaces', 'providedInterfaces']; // bidirectional / unstated
  }
  if (targetType === 'Application') return ['children'];
  const opts = relationsTargeting(targetType);
  return opts.length ? [opts[0].key] : [];
}

// ---------------------------------------------------------------------------
// Free-text scanning. Matching fact-sheet names inside prose is where false
// positives come from, so the bar to even be searched for is deliberately high:
// a name must be long enough to be distinctive and must not be a word that
// appears in ordinary sentences about IT.
// ---------------------------------------------------------------------------
const TEXT_FIELDS = [
  'cmOtherIntegrations',
  'cmExternalIntegrations',
  'appPurpose',
  'description',
  'serverInventory',
];

// Human labels for the scanned fields, straight off the Application schema, so
// the evidence column says "CM: Other Integrations?" and not "cmOtherIntegrations".
const FIELD_LABELS = (() => {
  const m = {};
  allFieldsFor('Application').forEach((f) => { if (!m[f.key]) m[f.key] = f.label; });
  return m;
})();

const MIN_NAME_LEN = 5;
const COMMON_NAMES = new Set([
  'access', 'admin', 'analytics', 'application', 'applications', 'archive', 'backup',
  'billing', 'cloud', 'data', 'database', 'email', 'engine', 'imaging', 'intranet',
  'inventory', 'legacy', 'mail', 'main', 'network', 'office', 'other', 'platform',
  'portal', 'print', 'printing', 'remote', 'report', 'reports', 'reporting', 'scanner',
  'security', 'server', 'servers', 'service', 'services', 'shared', 'storage', 'support',
  'system', 'systems', 'teams', 'test', 'users', 'vendor', 'web', 'windows', 'sql',
]);

const escapeRe = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Distinctive enough to hunt for in prose?
function isSearchableName(name) {
  const n = norm(name);
  if (n.length < MIN_NAME_LEN) return false;
  if (!/[a-z]/.test(n)) return false;
  if (COMMON_NAMES.has(n)) return false;
  // A single short generic-looking word is not worth the false positives.
  if (!n.includes(' ') && n.length < 6) return false;
  return true;
}

function snippetAround(text, index, length) {
  const start = Math.max(0, index - 45);
  const end = Math.min(text.length, index + length + 45);
  return `${start > 0 ? '…' : ''}${text.slice(start, end).trim()}${end < text.length ? '…' : ''}`;
}

// Total links currently on a fact sheet, across every relation key.
function linkCount(fs) {
  const rel = (fs && fs.relations) || {};
  return Object.keys(rel).reduce((n, k) => n + ((rel[k] || []).length), 0);
}

const hasLink = (fs, relKey, targetId) =>
  (((fs && fs.relations) || {})[relKey] || []).some((r) => r && r.id === targetId);

const CONF_STYLES = [
  { min: 0.8, label: 'high', color: GREEN, bg: '#E8F5E9' },
  { min: 0.6, label: 'medium', color: AMBER, bg: '#FFF4E0' },
  { min: 0, label: 'low', color: '#B0342C', bg: '#FDECEA' },
];
const confStyle = (c) => CONF_STYLES.find((s) => c >= s.min) || CONF_STYLES[CONF_STYLES.length - 1];

const RULE_LABELS = {
  integration: 'Integration row',
  engine: 'Interface engine',
  text: 'Free-text mention',
  org: 'Shared owner',
  ai: 'AI suggestion',
};

// Normalise whatever the model calls confidence into 0..1, weakest by default.
function aiConfidence(raw) {
  const s = String(raw == null ? '' : raw).toLowerCase();
  if (s.startsWith('h')) return 0.75;
  if (s.startsWith('m')) return 0.55;
  if (s.startsWith('l')) return 0.35;
  const n = Number(raw);
  if (!Number.isNaN(n) && n > 0) return Math.min(1, n > 1 ? n / 100 : n);
  return 0.35;
}

// ---------------------------------------------------------------------------
// STEP 1 — everything derivable from the tenant's own data. Pure function of
// the fact-sheet list: no network, no model, no randomness.
// ---------------------------------------------------------------------------
export function inferCandidates(rows) {
  const apps = rows.filter((fs) => fs && fs.type === 'Application');
  const byName = new Map();
  rows.forEach((fs) => { if (fs && fs.name && !byName.has(norm(fs.name))) byName.set(norm(fs.name), fs); });

  const out = new Map();      // dedupe key -> candidate (highest confidence wins)
  const seenLinked = new Set(); // links the evidence points at that already exist
  const unmatched = new Set();      // integration targets with no fact sheet
  const unmatchedOrgs = new Set();  // owner values with no Organization fact sheet

  const push = (source, target, relKey, rule, reason, confidence) => {
    if (!relKey || !target || target.id === source.id) return;
    const key = `${source.id}|${relKey}|${target.id}`;
    if (hasLink(source, relKey, target.id)) { seenLinked.add(key); return; }
    const prior = out.get(key);
    if (prior && prior.confidence >= confidence) return;
    out.set(key, {
      key,
      sourceId: source.id,
      sourceName: source.name,
      relKey,
      targetId: target.id,
      targetName: target.name,
      targetType: target.type,
      origin: 'inferred',
      rule,
      reason,
      confidence,
    });
  };

  // ---- (c) prep: the names distinctive enough to hunt for in prose ---------
  // Precomputed once. The cheap `includes` guard runs first so a 400-app tenant
  // does not pay for a regex per (application × field × fact sheet).
  const searchTargets = rows
    .filter((fs) => fs && fs.name && isSearchableName(fs.name) && relationsTargeting(fs.type).length)
    .map((fs) => ({
      fs,
      lower: norm(fs.name),
      re: new RegExp(`(^|[^a-z0-9])(${escapeRe(fs.name)})([^a-z0-9]|$)`, 'i'),
    }));

  // ---- (b) how many applications route through each `via` engine -----------
  const engineUsers = new Map();
  apps.forEach((app) => (app.integrations || []).forEach((it) => {
    const via = norm(it && it.via);
    if (!via) return;
    const set = engineUsers.get(via) || new Set();
    set.add(app.id);
    engineUsers.set(via, set);
  }));

  apps.forEach((app) => {
    // ---- (a) integration rows --------------------------------------------
    (app.integrations || []).forEach((it) => {
      if (!it) return;
      const to = clean(it.to);
      const via = clean(it.via);
      const detail = [it.protocol, it.port, it.direction, it.dataType].map(clean).filter(Boolean).join(' · ');

      if (to) {
        const target = byName.get(norm(to));
        if (target) {
          const reason = `Integration row on ${app.name}: “to: ${to}”${via ? ` via ${via}` : ''}${detail ? ` (${detail})` : ''}`;
          defaultRelationsFor(target.type, it.direction).forEach((relKey) =>
            push(app, target, relKey, 'integration', reason, 0.95));
        } else {
          unmatched.add(to);
        }
      }

      // ---- (b) the engine itself ------------------------------------------
      if (via) {
        const engine = byName.get(norm(via));
        if (engine) {
          const shared = (engineUsers.get(norm(via)) || new Set()).size;
          const reason = `Routes through “${via}”${to ? ` on the feed to ${to}` : ''}`
            + `${shared > 1 ? ` — shared by ${shared} applications, so the engine is a common dependency` : ''}`;
          const confidence = shared > 1 ? 0.9 : 0.85;
          defaultRelationsFor(engine.type, it.direction).forEach((relKey) =>
            push(app, engine, relKey, 'engine', reason, confidence));
        } else {
          unmatched.add(via);
        }
      }
    });

    // ---- (c) free text ----------------------------------------------------
    const fields = app.fields || {};
    const textKeys = [...TEXT_FIELDS, ...Object.keys(fields).filter((k) => k.startsWith('cf_'))];
    textKeys.forEach((fieldKey) => {
      const raw = fields[fieldKey];
      if (typeof raw !== 'string') return;
      const text = raw.trim();
      if (text.length < MIN_NAME_LEN) return;
      const lower = text.toLowerCase();
      const dedicated = fieldKey === 'cmOtherIntegrations' || fieldKey === 'cmExternalIntegrations';
      searchTargets.forEach((t) => {
        if (t.fs.id === app.id) return;
        if (!lower.includes(t.lower)) return;          // cheap guard before the regex
        const m = t.re.exec(text);
        if (!m) return;                                // substring, but not a whole word
        const at = m.index + m[1].length;
        const confidence = dedicated ? 0.8 : (t.lower.includes(' ') ? 0.65 : 0.55);
        const reason = `${FIELD_LABELS[fieldKey] || fieldKey}: “${snippetAround(text, at, t.fs.name.length)}”`;
        defaultRelationsFor(t.fs.type, '').forEach((relKey) =>
          push(app, t.fs, relKey, 'text', reason, confidence));
      });
    });

    // ---- (d) shared owner / business unit ---------------------------------
    const managedBy = (app.tags || []).find((t) => t && t.group === 'Managed By');
    const owners = [
      { value: clean(managedBy && managedBy.value), label: "'Managed By' tag", base: 0.7 },
      { value: clean(fields.businessUnit), label: 'Functional Business Unit', base: 0.6 },
    ].filter((o) => o.value);
    owners.forEach((o) => {
      const target = byName.get(norm(o.value));
      if (!target || target.type !== 'Organization') {
        if (!target) unmatchedOrgs.add(o.value);
        return;
      }
      const peers = apps.filter((a) => {
        const mb = (a.tags || []).find((t) => t && t.group === 'Managed By');
        return norm(mb && mb.value) === norm(o.value) || norm((a.fields || {}).businessUnit) === norm(o.value);
      }).length;
      const reason = `${o.label} = “${o.value}”, which is an Organization fact sheet`
        + `${peers > 1 ? ` shared with ${peers - 1} other application${peers - 1 === 1 ? '' : 's'}` : ''}`;
      push(app, target, 'organizations', 'org', reason, Math.min(0.9, o.base + (peers > 1 ? 0.05 : 0)));
    });
  });

  const candidates = [...out.values()].sort(
    (a, b) => a.sourceName.localeCompare(b.sourceName) || b.confidence - a.confidence
      || a.targetName.localeCompare(b.targetName)
  );
  return {
    candidates,
    alreadyLinked: seenLinked.size,
    unmatched: [...unmatched].sort((a, b) => a.localeCompare(b)),
    unmatchedOrgs: [...unmatchedOrgs].sort((a, b) => a.localeCompare(b)),
  };
}

// Group a flat candidate list by its source application. Module scope so it is
// a stable reference for the hooks that depend on it.
function groupBySource(list) {
  const byApp = new Map();
  list.forEach((c) => {
    const g = byApp.get(c.sourceId);
    if (g) g.rows.push(c);
    else byApp.set(c.sourceId, { sourceId: c.sourceId, sourceName: c.sourceName, rows: [c] });
  });
  return [...byApp.values()];
}

// ---------------------------------------------------------------------------

export default function RelationshipWizard({ tenantId, onOpen }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState('');

  const [search, setSearch] = useState('');
  const [threshold, setThreshold] = useState(70);
  const [accepted, setAccepted] = useState(() => new Set());
  const [overrides, setOverrides] = useState({});   // candidate key -> relation key

  const [aiRunning, setAiRunning] = useState(false);
  const [aiProgress, setAiProgress] = useState(0);
  const [aiUnavailable, setAiUnavailable] = useState(false);
  const [aiErr, setAiErr] = useState('');
  const [aiRows, setAiRows] = useState([]);
  const [aiDiscarded, setAiDiscarded] = useState(0);

  const [confirm, setConfirm] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyProgress, setApplyProgress] = useState(0);
  const [summary, setSummary] = useState(null);

  // ---- data ---------------------------------------------------------------
  const reload = useCallback(() => {
    setLoading(true);
    setLoadErr('');
    store.listFactSheets(tenantId)
      .then((list) => setRows(list || []))
      .catch(() => setLoadErr('Could not load fact sheets for this workspace.'))
      .finally(() => setLoading(false));
  }, [tenantId]);

  useEffect(() => {
    setAccepted(new Set());
    setOverrides({});
    setAiRows([]);
    setAiDiscarded(0);
    setAiUnavailable(false);
    setAiErr('');
    setSummary(null);
    reload();
  }, [reload]);

  const api = useCallback(async (path, options = {}) => {
    const res = await fetch(`${getConfig().apiUrl}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getIdToken()}`,
        ...(options.headers || {}),
      },
    });
    const text = await res.text();
    let data = {};
    try { data = text ? JSON.parse(text) : {}; } catch (e) { data = {}; }
    if (!res.ok) {
      const err = new Error(data.error || data.message || `request failed (${res.status})`);
      err.status = res.status;
      throw err;
    }
    return data;
  }, []);

  // ---- step 1 --------------------------------------------------------------
  const apps = useMemo(() => rows.filter((fs) => fs.type === 'Application'), [rows]);
  const appsById = useMemo(() => new Map(apps.map((a) => [a.id, a])), [apps]);
  const inferred = useMemo(() => inferCandidates(rows), [rows]);

  const allCandidates = useMemo(
    () => [...inferred.candidates, ...aiRows],
    [inferred.candidates, aiRows]
  );
  const relKeyOf = useCallback((c) => overrides[c.key] || c.relKey, [overrides]);

  // ---- coverage ------------------------------------------------------------
  const coverage = useMemo(() => {
    const withNone = apps.filter((a) => linkCount(a) === 0);
    const proposedFor = new Set(allCandidates.map((c) => c.sourceId));
    const emptyWithProposal = withNone.filter((a) => proposedFor.has(a.id));
    return {
      total: apps.length,
      empty: withNone.length,
      proposable: proposedFor.size,
      emptyProposable: emptyWithProposal.length,
      candidates: allCandidates.length,
    };
  }, [apps, allCandidates]);

  const ruleCounts = useMemo(() => {
    const m = {};
    allCandidates.forEach((c) => { m[c.rule] = (m[c.rule] || 0) + 1; });
    return m;
  }, [allCandidates]);

  // ---- filtering + grouping (display only) ---------------------------------
  const matches = useCallback((c) => {
    const q = norm(search);
    if (!q) return true;
    return norm(c.sourceName).includes(q) || norm(c.targetName).includes(q) || norm(c.reason).includes(q);
  }, [search]);

  const inferredVisible = useMemo(
    () => inferred.candidates.filter(matches),
    [inferred.candidates, matches]
  );
  const aiVisible = useMemo(() => aiRows.filter(matches), [aiRows, matches]);
  const inferredGroups = useMemo(() => groupBySource(inferredVisible), [inferredVisible]);
  const aiGroups = useMemo(() => groupBySource(aiVisible), [aiVisible]);

  // ---- selection -----------------------------------------------------------
  const toggle = (key) => {
    const next = new Set(accepted);
    if (next.has(key)) next.delete(key); else next.add(key);
    setAccepted(next);
    setSummary(null);
  };
  const toggleGroup = (group) => {
    const all = group.rows.every((c) => accepted.has(c.key));
    const next = new Set(accepted);
    group.rows.forEach((c) => { if (all) next.delete(c.key); else next.add(c.key); });
    setAccepted(next);
    setSummary(null);
  };

  // Bulk accept works on the FULL inferred set, not the visible slice.
  const overThreshold = useMemo(
    () => inferred.candidates.filter((c) => pct(c.confidence) >= threshold),
    [inferred.candidates, threshold]
  );
  const acceptOverThreshold = () => {
    const next = new Set(accepted);
    overThreshold.forEach((c) => next.add(c.key));
    setAccepted(next);
    setSummary(null);
  };
  const clearAll = () => { setAccepted(new Set()); setSummary(null); };

  const acceptedRows = useMemo(
    () => allCandidates.filter((c) => accepted.has(c.key)),
    [allCandidates, accepted]
  );
  const acceptedByRelation = useMemo(() => {
    const m = new Map();
    acceptedRows.forEach((c) => {
      const k = relKeyOf(c);
      m.set(k, (m.get(k) || 0) + 1);
    });
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [acceptedRows, relKeyOf]);
  const acceptedAppCount = new Set(acceptedRows.map((c) => c.sourceId)).size;
  const acceptedAiCount = acceptedRows.filter((c) => c.origin === 'ai').length;

  // ---- step 2 — AI ---------------------------------------------------------
  // The model is handed a closed list of targets. Anything it names that is not
  // on that list is dropped here, before it can reach the review table.
  const targetCatalog = useMemo(
    () => rows
      .filter((fs) => fs && fs.name && relationsTargeting(fs.type).length)
      .slice(0, AI_TARGET_CAP)
      .map((fs) => ({ id: fs.id, name: fs.name, type: fs.type })),
    [rows]
  );

  const runAi = async () => {
    if (!apps.length || !targetCatalog.length) return;
    setAiRunning(true);
    setAiErr('');
    setAiUnavailable(false);
    setAiProgress(0);
    setAiRows([]);
    setAiDiscarded(0);
    setSummary(null);

    const byId = new Map(targetCatalog.map((t) => [t.id, t]));
    const byName = new Map(targetCatalog.map((t) => [norm(t.name), t]));
    const collected = [];
    // A const counter object, so the per-chunk callbacks below never close over
    // a reassigned loop-scope binding.
    const counters = { discarded: 0 };

    try {
      for (let i = 0; i < apps.length; i += AI_CHUNK) {
        const chunk = apps.slice(i, i + AI_CHUNK);
        const payload = {
          applications: chunk.map((fs) => ({
            id: fs.id,
            name: fs.name,
            description: (fs.fields || {}).description || (fs.fields || {}).appPurpose || '',
          })),
          // The closed world the model must choose from.
          targets: targetCatalog,
          relationTypes: APPLICATION_RELATIONS.map((r) => ({ key: r.key, label: r.label, target: r.target })),
          instruction: 'Only propose targets from the provided targets list. Never invent a target.',
        };
        /* eslint-disable-next-line no-await-in-loop */
        const data = await api('/ai/suggest-relations', { method: 'POST', body: JSON.stringify(payload) });
        (data.results || []).forEach((r) => {
          const app = chunk.find((a) => a.id === (r && r.id));
          if (!app) { counters.discarded += 1; return; }
          (r.suggestions || []).forEach((s) => {
            const target = byId.get(s && s.targetId) || byName.get(norm(s && s.targetName));
            if (!target || target.id === app.id) { counters.discarded += 1; return; }   // invented target
            const legal = relationsTargeting(target.type).map((d) => d.key);
            const relKey = legal.includes(s && s.relationType)
              ? s.relationType
              : (defaultRelationsFor(target.type, '')[0] || '');
            if (!relKey || !legal.includes(relKey)) { counters.discarded += 1; return; }
            if (hasLink(app, relKey, target.id)) return;                        // already linked
            const key = `ai|${app.id}|${relKey}|${target.id}`;
            if (collected.some((c) => c.key === key)) return;
            collected.push({
              key,
              sourceId: app.id,
              sourceName: app.name,
              relKey,
              targetId: target.id,
              targetName: target.name,
              targetType: target.type,
              origin: 'ai',
              rule: 'ai',
              reason: clean(s.rationale) || 'No rationale supplied by the model.',
              confidence: aiConfidence(s.confidence),
            });
          });
        });
        setAiProgress(Math.round(Math.min(100, ((i + chunk.length) / apps.length) * 100)));
        /* eslint-disable-next-line no-await-in-loop */
        await new Promise((r) => setTimeout(r, 0));
      }
      collected.sort((a, b) => a.sourceName.localeCompare(b.sourceName) || b.confidence - a.confidence);
      setAiRows(collected);
      setAiDiscarded(counters.discarded);
      if (!collected.length) setAiErr('The service returned no usable suggestions for this workspace.');
    } catch (e) {
      setAiUnavailable(true);
      setAiErr((e && e.message) || 'The suggestion service could not be reached.');
    } finally {
      setAiRunning(false);
    }
  };

  // ---- step 3 — apply ------------------------------------------------------
  const applyAccepted = async () => {
    if (!acceptedRows.length) return;
    setConfirm(false);
    setApplying(true);
    setApplyProgress(0);
    setSummary(null);

    const jobs = groupBySource(acceptedRows);
    const stampedAt = new Date().toISOString();
    let okApps = 0;
    let links = 0;
    let skipped = 0;
    let failed = 0;
    const errors = [];

    for (let i = 0; i < jobs.length; i++) {
      const job = jobs[i];
      const fs = appsById.get(job.sourceId);
      if (!fs) { failed += 1; continue; }
      const relations = { ...(fs.relations || {}) };
      let addedHere = 0;
      let skippedHere = 0;
      job.rows.forEach((c) => {
        const relKey = overrides[c.key] || c.relKey;
        const def = relationMeta(relKey);
        const existing = [...(relations[relKey] || [])];
        // never a duplicate…
        if (existing.some((e) => e && e.id === c.targetId)) { skippedHere += 1; return; }
        // …and never clobber a single-valued relation that is already answered.
        if (def && def.single && existing.length) { skippedHere += 1; return; }
        existing.push({
          id: c.targetId,
          attributes: {
            relationSource: c.origin,          // 'inferred' | 'ai' — auditable origin
            relationRule: c.rule,
            relationConfidence: pct(c.confidence),
            relationProposedAt: stampedAt,
            // notes is a first-class relation attribute (RelationEditor.js), so
            // the evidence survives later hand-editing of the connection.
            notes: `${c.origin === 'ai' ? 'AI suggestion' : 'Inferred by Relationship Wizard'} — ${c.reason}`,
          },
        });
        relations[relKey] = existing;
        addedHere += 1;
      });
      skipped += skippedHere;
      if (!addedHere) { setApplyProgress(Math.round(((i + 1) / jobs.length) * 100)); continue; }
      try {
        /* eslint-disable-next-line no-await-in-loop */
        await store.saveFactSheet({ ...fs, relations });
        okApps += 1;
        links += addedHere;
      } catch (err) {
        failed += 1;
        if (errors.length < 5) errors.push(`${job.sourceName}: ${(err && err.message) || 'save failed'}`);
      }
      setApplyProgress(Math.round(((i + 1) / jobs.length) * 100));
      /* eslint-disable-next-line no-await-in-loop */
      await new Promise((r) => setTimeout(r, 0));
    }

    setApplying(false);
    setSummary({ okApps, links, skipped, failed, errors });
    setAccepted(new Set());
    setAiRows(aiRows.filter((c) => !acceptedRows.some((a) => a.key === c.key)));
    reload();
  };

  const busy = aiRunning || applying;

  // ---- render helpers ------------------------------------------------------
  const NameLink = ({ id, name, type }) => {
    const inner = (
      <Stack direction="row" spacing={0.75} alignItems="center" component="span">
        {type ? <TypeBadge type={type} size={16} /> : null}
        <span>{name}</span>
      </Stack>
    );
    if (!id || !onOpen) return <Box component="span" sx={{ color: GREY }}>{inner}</Box>;
    return (
      <Box
        component="span"
        role="button"
        tabIndex={0}
        onClick={() => onOpen(id)}
        onKeyDown={(e) => { if (e.key === 'Enter') onOpen(id); }}
        sx={{ color: BLUE, fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
      >
        {inner}
      </Box>
    );
  };

  const ConfidenceChip = ({ value }) => {
    const s = confStyle(value);
    return (
      <Chip
        size="small"
        label={`${s.label} · ${pct(value)}%`}
        sx={{ height: 20, fontSize: 11, fontWeight: 700, color: s.color, bgcolor: s.bg, border: `1px solid ${s.color}33` }}
      />
    );
  };

  const Tile = ({ label, value, hint, tone }) => (
    <Paper variant="outlined" sx={{ p: 1.5, minWidth: 150, flex: '1 1 150px', borderColor: '#e2e8f0' }}>
      <Typography sx={{ fontSize: 11, color: GREY, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 26, fontWeight: 700, color: tone || NAVY, lineHeight: 1.2 }}>{value}</Typography>
      {hint ? <Typography sx={{ fontSize: 11, color: GREY }}>{hint}</Typography> : null}
    </Paper>
  );

  const RelationCell = ({ candidate }) => {
    const options = relationsTargeting(candidate.targetType);
    const value = relKeyOf(candidate);
    if (options.length <= 1) {
      return (
        <Typography sx={{ fontSize: 12.5, color: NAVY, fontWeight: 600, whiteSpace: 'nowrap' }}>
          {(relationMeta(value) || {}).label || value}
        </Typography>
      );
    }
    return (
      <TextField
        select
        size="small"
        variant="standard"
        value={value}
        disabled={busy}
        onChange={(e) => setOverrides({ ...overrides, [candidate.key]: e.target.value })}
        sx={{ minWidth: 170, '& .MuiInputBase-input': { fontSize: 12.5, fontWeight: 600, color: NAVY } }}
      >
        {options.map((o) => <MenuItem key={o.key} value={o.key} sx={{ fontSize: 13 }}>{o.label}</MenuItem>)}
      </TextField>
    );
  };

  // One grouped candidate table, capped at ROW_CAP rendered rows.
  const CandidateTable = ({ groups, total, emptyText }) => {
    let rendered = 0;
    const shown = [];
    groups.forEach((g) => {
      if (rendered >= ROW_CAP) return;
      const slice = g.rows.slice(0, Math.max(0, ROW_CAP - rendered));
      rendered += slice.length;
      shown.push({ ...g, rows: slice });
    });
    return (
      <Paper variant="outlined" sx={{ maxHeight: 520, overflow: 'auto', borderColor: '#e2e8f0' }}>
        <Table size="small" stickyHeader sx={{ '& td': { fontSize: 12.5 } }}>
          <TableHead>
            <TableRow sx={headSx}>
              <TableCell padding="checkbox" />
              <TableCell>Relation</TableCell>
              <TableCell>Target</TableCell>
              <TableCell>Evidence</TableCell>
              <TableCell>Confidence</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shown.map((g) => {
              const allOn = g.rows.every((c) => accepted.has(c.key));
              const someOn = g.rows.some((c) => accepted.has(c.key));
              const source = appsById.get(g.sourceId);
              return (
                <React.Fragment key={g.sourceId}>
                  <TableRow sx={{ bgcolor: '#F4F7FA' }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small" checked={allOn} indeterminate={!allOn && someOn}
                        onChange={() => toggleGroup(g)} disabled={busy}
                      />
                    </TableCell>
                    <TableCell colSpan={4} sx={{ fontWeight: 700, color: NAVY }}>
                      <NameLink id={g.sourceId} name={g.sourceName} type="Application" />
                      <Typography component="span" variant="caption" sx={{ color: GREY, ml: 1 }}>
                        {g.rows.length} proposal{g.rows.length === 1 ? '' : 's'}
                        {source && linkCount(source) === 0 ? ' · no relations today' : ''}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {g.rows.map((c) => (
                    <TableRow key={c.key} hover selected={accepted.has(c.key)}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small" checked={accepted.has(c.key)}
                          onChange={() => toggle(c.key)} disabled={busy}
                        />
                      </TableCell>
                      <TableCell><RelationCell candidate={c} /></TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <NameLink id={c.targetId} name={c.targetName} type={c.targetType} />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 520, color: GREY }}>
                        <Chip
                          size="small"
                          label={RULE_LABELS[c.rule] || c.rule}
                          sx={{
                            height: 18, fontSize: 10, fontWeight: 700, mr: 0.75,
                            bgcolor: c.origin === 'ai' ? '#EEF4FB' : '#eef3f9',
                            color: c.origin === 'ai' ? BLUE : NAVY,
                            border: c.origin === 'ai' ? `1px solid ${BLUE}55` : 'none',
                          }}
                        />
                        <Tooltip title={c.reason}>
                          <Box component="span" sx={{ borderBottom: '1px dotted #9AA6B2', cursor: 'help' }}>
                            {c.reason.length > 130 ? `${c.reason.slice(0, 130)}…` : c.reason}
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell><ConfidenceChip value={c.confidence} /></TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              );
            })}
            {!shown.length && (
              <TableRow>
                <TableCell colSpan={5} sx={{ color: GREY, textAlign: 'center', py: 4 }}>{emptyText}</TableCell>
              </TableRow>
            )}
            {rendered < total && (
              <TableRow>
                <TableCell colSpan={5} sx={{ color: GREY, textAlign: 'center', py: 1.5, fontStyle: 'italic' }}>
                  showing the first {rendered} of {total} proposals — the threshold accept and the apply
                  step still cover all of them
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    );
  };

  // ---- render --------------------------------------------------------------
  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5, flexWrap: 'wrap' }}>
        <AccountTreeIcon sx={{ color: BLUE }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: NAVY }}>Relationship wizard</Typography>
        <Chip
          size="small" label="EVIDENCE-BASED — REVIEW REQUIRED"
          sx={{ height: 20, fontWeight: 700, bgcolor: '#eef3f9', color: NAVY, border: `1px solid ${NAVY}33` }}
        />
        <Box sx={{ flex: 1 }} />
        <Button size="small" startIcon={<RefreshIcon />} onClick={reload} disabled={busy}>Reload</Button>
      </Stack>
      <Typography variant="body2" sx={{ color: GREY, mb: 2, maxWidth: 980 }}>
        The relationships are already in this workspace — in the integration rows, in the interface
        engine each feed routes through, in free-text answers like <i>“imports codes into Accounts in
        Epic”</i>, and in the owner tags. This screen turns them into links. Every proposal shows the
        exact evidence it came from, and <b>nothing is written until you press Apply</b>.
      </Typography>

      {loadErr && <Alert severity="error" sx={{ mb: 2 }}>{loadErr}</Alert>}

      {/* ---------------- coverage strip — the reason this screen exists ----- */}
      <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
        <Tile label="Applications" value={coverage.total} />
        <Tile
          label="With zero relations" value={coverage.empty}
          tone={coverage.empty ? '#B0342C' : GREEN}
          hint={coverage.total ? `${Math.round((coverage.empty / coverage.total) * 100)}% of the portfolio` : ''}
        />
        <Tile
          label="Wizard can propose for" value={coverage.emptyProposable}
          tone={coverage.emptyProposable ? GREEN : GREY}
          hint="of the applications that have none"
        />
        <Tile label="Candidate links" value={coverage.candidates} hint={`${coverage.proposable} applications covered`} />
        <Tile label="Already linked" value={inferred.alreadyLinked} hint="found again, skipped as duplicates" />
      </Stack>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {!loading && !coverage.total && (
        <Paper sx={{ p: 6, textAlign: 'center', color: GREY }}>
          <HubIcon sx={{ fontSize: 44, color: '#c7d3e0' }} />
          <Typography sx={{ fontWeight: 700, color: HEAD, mt: 1 }}>No applications in this workspace yet</Typography>
        </Paper>
      )}

      {!loading && !!coverage.total && (
        <>
          {/* ------------- 1 · what we can infer ---------------------------- */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}>
              <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700 }}>
                1 · What we can infer from your data
              </Typography>
              <Chip size="small" label="no AI — deterministic" sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: '#E8F5E9', color: GREEN }} />
              <Box sx={{ flex: 1 }} />
              {Object.keys(RULE_LABELS).filter((k) => k !== 'ai' && ruleCounts[k]).map((k) => (
                <Chip key={k} size="small" label={`${RULE_LABELS[k]} · ${ruleCounts[k]}`}
                  sx={{ height: 20, fontSize: 11, fontWeight: 600, bgcolor: '#eef3f9', color: NAVY }} />
              ))}
            </Stack>
            <Typography variant="caption" sx={{ color: GREY, display: 'block', mb: 1.5 }}>
              Derived from integration rows (<code>to</code> / <code>via</code>), shared interface engines,
              name mentions inside free-text fields, and the owner tag. Only targets that already exist as
              fact sheets in this workspace are ever proposed.
            </Typography>

            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1.5 }}>
              <TextField
                size="small" placeholder="Search application, target or evidence…"
                value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 260 }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              />
              <Box sx={{ minWidth: 260 }}>
                <Typography variant="caption" sx={{ color: GREY }}>
                  Bulk accept at or above <b>{threshold}%</b> confidence
                </Typography>
                <Slider
                  size="small" value={threshold} min={30} max={100} step={5}
                  onChange={(e, v) => setThreshold(Array.isArray(v) ? v[0] : v)}
                  disabled={busy} sx={{ color: BLUE }}
                />
              </Box>
              <Button
                variant="contained" size="small" disabled={busy || !overThreshold.length}
                onClick={acceptOverThreshold} sx={{ bgcolor: BLUE, '&:hover': { bgcolor: NAVY } }}
              >
                Accept {overThreshold.length} inferred link{overThreshold.length === 1 ? '' : 's'}
              </Button>
              <Button size="small" onClick={clearAll} disabled={busy || !accepted.size}>Clear all</Button>
            </Stack>

            <CandidateTable
              groups={inferredGroups}
              total={inferredVisible.length}
              emptyText={inferred.candidates.length
                ? 'No proposals match this search.'
                : 'Nothing could be inferred yet — capture integrations on the fact sheets, or answer “CM: Other Integrations?”, and this fills up.'}
            />

            {(!!inferred.unmatched.length || !!inferred.unmatchedOrgs.length) && (
              <Typography variant="caption" sx={{ color: GREY, display: 'block', mt: 1.5 }}>
                {inferred.unmatched.length > 0 && (
                  <>
                    {inferred.unmatched.length} integration target{inferred.unmatched.length === 1 ? '' : 's'} named on
                    fact sheets have no fact sheet of their own, so they cannot be linked
                    (e.g. {inferred.unmatched.slice(0, 4).map((n) => `“${n}”`).join(', ')}). Create them and re-run.
                  </>
                )}
                {inferred.unmatchedOrgs.length > 0 && (
                  <>
                    {' '}{inferred.unmatchedOrgs.length} owner value{inferred.unmatchedOrgs.length === 1 ? '' : 's'} have
                    no matching Organization fact sheet.
                  </>
                )}
              </Typography>
            )}
          </Paper>

          {/* ------------- 2 · optional AI ---------------------------------- */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: `${BLUE}55` }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}>
              <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700 }}>
                2 · Ask AI to suggest (optional)
              </Typography>
              <Chip size="small" icon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />} label="AI-SUGGESTED"
                sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: '#EEF4FB', color: BLUE, border: `1px solid ${BLUE}44` }} />
              <Box sx={{ flex: 1 }} />
              <Button
                variant="outlined" size="small" startIcon={<AutoAwesomeIcon />}
                onClick={runAi} disabled={busy || !targetCatalog.length}
                sx={{ color: BLUE, borderColor: BLUE }}
              >
                {aiRunning ? `Asking ${aiProgress}%` : `Ask AI about ${apps.length} application${apps.length === 1 ? '' : 's'}`}
              </Button>
            </Stack>
            <Typography variant="caption" sx={{ color: GREY, display: 'block', mb: 1 }}>
              The model is sent each application's name and description together with the{' '}
              <b>{targetCatalog.length} candidate target names already in this workspace</b>, and may only
              choose from that list — anything else it names is discarded before you see it. AI rows are
              marked with their own chip and start <b>unchecked</b>. Step 1 above is the valuable part and
              does not depend on this.
            </Typography>
            {aiRunning && <LinearProgress variant="determinate" value={aiProgress} sx={{ mb: 1.5 }} />}

            {aiUnavailable && (
              <Alert severity="info" icon={<InfoOutlinedIcon fontSize="small" />} sx={{ mb: 1.5 }}>
                <AlertTitle>AI suggestions are not enabled for this environment</AlertTitle>
                The <code>/ai/suggest-relations</code> service did not respond{aiErr ? ` (${aiErr})` : ''}.
                Nothing was changed, and everything inferred in step 1 still works.
              </Alert>
            )}
            {!aiUnavailable && aiErr && (
              <Alert severity="warning" sx={{ mb: 1.5 }} onClose={() => setAiErr('')}>{aiErr}</Alert>
            )}
            {aiDiscarded > 0 && (
              <Alert severity="warning" sx={{ mb: 1.5 }}>
                <b>{aiDiscarded}</b> suggestion{aiDiscarded === 1 ? '' : 's'} discarded because they named a
                target or relation type that does not exist in this workspace.
              </Alert>
            )}

            {!!aiRows.length && (
              <CandidateTable groups={aiGroups} total={aiVisible.length} emptyText="No AI suggestions match this search." />
            )}
          </Paper>

          {/* ------------- 3 · review & apply ------------------------------- */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
              <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700 }}>3 · Review &amp; apply</Typography>
              <Chip size="small" label={`${acceptedRows.length} accepted`}
                sx={{ fontWeight: 700, height: 22, bgcolor: acceptedRows.length ? '#e3f0fa' : undefined }} />
              {acceptedAiCount > 0 && (
                <Chip size="small" label={`${acceptedAiCount} from AI`}
                  sx={{ fontWeight: 700, height: 22, bgcolor: '#EEF4FB', color: BLUE }} />
              )}
              <Box sx={{ flex: 1 }} />
              {acceptedByRelation.map(([k, n]) => (
                <Chip key={k} size="small" label={`${(relationMeta(k) || {}).label || k} · ${n}`}
                  sx={{ height: 20, fontSize: 11, fontWeight: 600, bgcolor: '#eef3f9', color: NAVY }} />
              ))}
            </Stack>

            {!acceptedRows.length ? (
              <Typography variant="body2" sx={{ color: GREY }}>
                Nothing accepted yet. Check individual proposals above, or use the confidence slider to
                accept every inferred link at or above a threshold in one go.
              </Typography>
            ) : (
              <>
                <Paper variant="outlined" sx={{ maxHeight: 360, overflow: 'auto', borderColor: '#e2e8f0' }}>
                  <Table size="small" stickyHeader sx={{ '& td': { fontSize: 12.5 } }}>
                    <TableHead>
                      <TableRow sx={headSx}>
                        <TableCell>Application</TableCell>
                        <TableCell>Relation</TableCell>
                        <TableCell>Target</TableCell>
                        <TableCell>Source</TableCell>
                        <TableCell>Confidence</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {acceptedRows.slice(0, ROW_CAP).map((c) => (
                        <TableRow key={c.key} hover>
                          <TableCell sx={{ fontWeight: 600, color: NAVY }}>{c.sourceName}</TableCell>
                          <TableCell sx={{ color: GREY, whiteSpace: 'nowrap' }}>
                            {(relationMeta(relKeyOf(c)) || {}).label || relKeyOf(c)}
                          </TableCell>
                          <TableCell><NameLink id={c.targetId} name={c.targetName} type={c.targetType} /></TableCell>
                          <TableCell>
                            <Tooltip title={c.reason}>
                              <Chip
                                size="small"
                                label={c.origin === 'ai' ? 'ai' : 'inferred'}
                                sx={{
                                  height: 18, fontSize: 10, fontWeight: 700,
                                  bgcolor: c.origin === 'ai' ? '#EEF4FB' : '#E8F5E9',
                                  color: c.origin === 'ai' ? BLUE : GREEN,
                                }}
                              />
                            </Tooltip>
                          </TableCell>
                          <TableCell><ConfidenceChip value={c.confidence} /></TableCell>
                        </TableRow>
                      ))}
                      {acceptedRows.length > ROW_CAP && (
                        <TableRow>
                          <TableCell colSpan={5} sx={{ color: GREY, textAlign: 'center', fontStyle: 'italic', py: 1.5 }}>
                            showing the first {ROW_CAP} of {acceptedRows.length} — all of them will be applied
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Paper>

                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1 }}>
                  <Button
                    variant="contained" disabled={applying} onClick={() => setConfirm(true)}
                    sx={{ bgcolor: NAVY, '&:hover': { bgcolor: BLUE } }}
                  >
                    {applying ? `Applying ${applyProgress}%` : `Apply ${acceptedRows.length} link${acceptedRows.length === 1 ? '' : 's'}`}
                  </Button>
                  <Typography variant="caption" sx={{ color: GREY }}>
                    across {acceptedAppCount} application{acceptedAppCount === 1 ? '' : 's'} · merged into the
                    existing relations, never replacing them
                  </Typography>
                </Stack>
                {applying && <LinearProgress variant="determinate" value={applyProgress} sx={{ mt: 1.5 }} />}
              </>
            )}
          </Paper>
        </>
      )}

      {summary && (
        <Alert severity={summary.failed ? 'warning' : 'success'} sx={{ mb: 2 }} onClose={() => setSummary(null)}>
          <AlertTitle>Applied</AlertTitle>
          Created {summary.links} link{summary.links === 1 ? '' : 's'} across {summary.okApps}{' '}
          application{summary.okApps === 1 ? '' : 's'}
          {summary.skipped ? ` · ${summary.skipped} skipped as duplicates or already-answered single relations` : ''}
          {summary.failed ? ` · ${summary.failed} could not be saved` : ''}.
          {!!summary.errors.length && (
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
              {summary.errors.map((e, i) => <li key={i}>{e}</li>)}
            </Box>
          )}
        </Alert>
      )}

      <Dialog open={confirm} onClose={() => !applying && setConfirm(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700, color: NAVY }}>Apply these relationships?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            This will add <b>{acceptedRows.length}</b> link{acceptedRows.length === 1 ? '' : 's'} across{' '}
            <b>{acceptedAppCount}</b> application{acceptedAppCount === 1 ? '' : 's'}. Existing relations are
            kept, a link that is already there is skipped, and a single-valued relation that is already
            answered is left alone.
          </Typography>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="caption" sx={{ color: GREY, fontWeight: 700, display: 'block', mb: 0.5 }}>
            By relation type
          </Typography>
          <Box component="ul" sx={{ mt: 0, mb: 1.5, pl: 2.5 }}>
            {acceptedByRelation.map(([k, n]) => (
              <li key={k}>
                <Typography component="span" variant="body2">
                  <b>{n}</b> · {(relationMeta(k) || {}).label || k}
                  <Typography component="span" variant="caption" sx={{ color: GREY, ml: 0.5 }}>
                    → {typeMeta((relationMeta(k) || {}).target).label}
                  </Typography>
                </Typography>
              </li>
            ))}
          </Box>
          {acceptedAiCount > 0 && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              <b>{acceptedAiCount}</b> of these came from the AI step. They are marked{' '}
              <code>relationSource: 'ai'</code> and should be confirmed with the application owner.
            </Alert>
          )}
          <FormControlLabel
            sx={{ m: 0 }}
            control={<Checkbox size="small" checked disabled />}
            label={(
              <Typography variant="caption" sx={{ color: GREY }}>
                Every link is stamped with <code>relationSource</code>, its rule, its confidence and the
                evidence text, so where it came from stays auditable.
              </Typography>
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(false)} disabled={applying}>Cancel</Button>
          <Button
            variant="contained" onClick={applyAccepted} disabled={applying}
            sx={{ bgcolor: NAVY, '&:hover': { bgcolor: BLUE } }}
          >
            Apply and save
          </Button>
        </DialogActions>
      </Dialog>

      {loading && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ color: GREY }}>
          <CircularProgress size={16} /><Typography variant="caption">Reading the workspace…</Typography>
        </Stack>
      )}
    </Box>
  );
}
